/**
 * Project scope for Process Mining. Navigation only: the process engine and
 * every resource service still enforce authorization.
 *
 * Processes are not placeable resources; they belong to a project through the
 * object types they read. An implicit process belongs when its object type
 * does; an explicit one when its process object or its event object does.
 * `null` means "no project open": everything is visible, as before.
 */
import { useMemo } from 'react';
import type { Process } from './api';
import type { WorkspacePlacement } from '../files/workspaceApi';
import { useWorkspaceStore } from '../files/workspaceStore';

export type ProjectObjectTypeIds = ReadonlySet<string> | null;

export function projectObjectTypeIds(placements: WorkspacePlacement[], projectId: string | null): ProjectObjectTypeIds {
  if (!projectId) return null;
  return new Set(placements.filter((p) => p.projectId === projectId && p.resourceType === 'object_type').map((p) => p.resourceId));
}

/** Every object type a process reads: included types plus its event source. */
export function processObjectTypeIds(process: Process): string[] {
  const src = process.event_source;
  const ids = [...(process.included_object_type_ids || []), src?.object_type_id, src?.process_object_type_id];
  return [...new Set(ids.filter((id): id is string => !!id))];
}

export function processInProject(process: Process, ids: ProjectObjectTypeIds): boolean {
  if (!ids) return true;
  return processObjectTypeIds(process).some((id) => ids.has(id));
}

/** `keepIds` are processes that stay visible regardless (created this session). */
export function scopeProcesses(processes: Process[], ids: ProjectObjectTypeIds, keepIds: ReadonlySet<string> | string[] = []): Process[] {
  if (!ids) return processes;
  const keep = keepIds instanceof Set ? keepIds : new Set(keepIds);
  return processes.filter((p) => keep.has(p.id) || processInProject(p, ids));
}

/** `keepIds` are object types that stay listed (the ones an open process already uses). */
export function scopeObjectTypes<T extends { id: string }>(objectTypes: T[], ids: ProjectObjectTypeIds, keepIds: string[] = []): T[] {
  if (!ids) return objectTypes;
  return objectTypes.filter((o) => ids.has(o.id) || keepIds.includes(o.id));
}

export function useProjectObjectTypeIds(): ProjectObjectTypeIds {
  const projectId = useWorkspaceStore((s) => s.projectId);
  const placements = useWorkspaceStore((s) => s.manifest.placements);
  return useMemo(() => projectObjectTypeIds(placements, projectId), [placements, projectId]);
}
